import type { z } from 'zod'
import {
  ApiError,
  Asked,
  StartedConversation,
  Transcript,
  type MountConfig,
} from './protocol'

// LF-918: the sidebar's HTTP calls. Every response is parsed; a failure comes
// back as a value carrying the server's error code, never as an exception.

export type Failure = { ok: false; status: number; error: string }
export type Outcome<T> = { ok: true; value: T } | Failure

const csrfToken = (): string =>
  document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.getAttribute('content') ?? ''

const request = async <T>(
  schema: z.ZodType<T>,
  method: 'GET' | 'POST',
  url: string,
  body?: unknown,
): Promise<Outcome<T>> => {
  let response: Response
  try {
    response = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    return { ok: false, status: 0, error: 'network' }
  }

  const json: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const parsed = ApiError.safeParse(json)
    return {
      ok: false,
      status: response.status,
      error: parsed.success ? parsed.data.error : 'unknown',
    }
  }
  const parsed = schema.safeParse(json)
  if (!parsed.success) {
    console.error('Unexpected chatbot response', parsed.error)
    return { ok: false, status: response.status, error: 'bad_response' }
  }
  return { ok: true, value: parsed.data }
}

export const startConversation = (config: MountConfig) =>
  request(StartedConversation, 'POST', '/chatbot/conversations', {
    pageKind: config.pageKind,
    pagePath: config.pagePath,
    ...(config.calcSessionId ? { calcSessionId: config.calcSessionId } : {}),
  })

export const ask = (conversationId: string, content: string) =>
  request(Asked, 'POST', `/chatbot/conversations/${conversationId}/messages`, {
    content,
  })

export const fetchTranscript = (conversationId: string) =>
  request(Transcript, 'GET', `/chatbot/conversations/${conversationId}`)
